import { useEffect, useState } from "react";
import { db, type SavedAnalysis } from "../../db";
import { useUiStore } from "../../stores/ui-store";
import { buildRunTrends, type RunTrends, type TrendAnalysis } from "./trends";

/**
 * Fill-rate trends for the source of the run on screen, read from the cache.
 *
 * Returns null while loading, and an empty result rather than an error when
 * IndexedDB is unavailable — a trend panel is context, and a browser that
 * cannot serve it must not take the Overview down with it.
 */
export function useRunTrends(): RunTrends | null {
  const analysis = useUiStore((state) => state.analysis);
  const review = useUiStore((state) => state.review);
  const profileId = review?.profileId ?? null;
  const currentAnalysisKey = analysis?.analysisKey ?? null;
  const [trends, setTrends] = useState<RunTrends | null>(null);

  // The run on screen comes from state, not the cache: the cache write races the
  // render, and a panel that missed it would say this is the only run of the
  // source seconds after analysing it.
  const current: TrendAnalysis | null =
    analysis === null || review === null
      ? null
      : {
          analysisKey: analysis.analysisKey,
          createdAt: review.generatedAt,
          result: { fieldStats: analysis.fieldStats },
          review: { profileId: review.profileId, generatedAt: review.generatedAt }
        };

  useEffect(() => {
    let cancelled = false;
    if (profileId === null) {
      setTrends(buildRunTrends({ analyses: [], profileId: null, current: null }));
      return;
    }
    void db.analyses
      .toArray()
      .catch(() => [] as SavedAnalysis[])
      .then((analyses) => {
        if (cancelled) return;
        setTrends(buildRunTrends({ analyses: analyses as never, profileId, current }));
      });
    return () => {
      cancelled = true;
    };
    // `current` is rebuilt every render; the run it describes changes only with
    // its key, which is what the analysis is keyed by.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, currentAnalysisKey]);

  return trends;
}
